import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Mail, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { Eyebrow, Reveal } from "./bits";
import { API, scrollToId } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

const fmtINR = (n) => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(n % 1e7 === 0 ? 0 : 2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(n % 1e5 === 0 ? 0 : 1)} L`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

const fmtFull = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

const sipFor = (fv, years, annualReturn) => {
  const i = annualReturn / 100 / 12;
  const n = years * 12;
  const factor = ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
  return fv / factor;
};

const seriesFor = (sip, years, annualReturn) => {
  const i = annualReturn / 100 / 12;
  const pts = [];
  const inv = [];
  for (let y = 0; y <= years; y += 1) {
    const m = y * 12;
    pts.push(m === 0 ? 0 : sip * ((Math.pow(1 + i, m) - 1) / i) * (1 + i));
    inv.push(sip * m);
  }
  return { pts, inv };
};

const Slider = ({ label, suffix, value, min, max, step, onChange, testId }) => {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);

  const pct = ((value - min) / (max - min)) * 100;

  const handleType = (raw) => {
    setText(raw);
    if (raw === "") return;
    const v = Number(raw);
    if (Number.isNaN(v)) return;
    if (v > max) onChange(max);
    else if (v >= min) onChange(v);
  };

  const handleBlur = () => {
    setFocused(false);
    const v = Number(text);
    if (text === "" || Number.isNaN(v) || v < min) onChange(min);
    else if (v > max) onChange(max);
    else onChange(v);
  };

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <label
          htmlFor={testId}
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E8698]"
        >
          {label}
        </label>
        <div className="flex items-baseline gap-2">
          <input
            type="number"
            data-testid={`${testId}-input`}
            value={focused ? text : String(value)}
            min={min}
            max={max}
            step={step}
            onFocus={() => {
              setText(String(value));
              setFocused(true);
            }}
            onChange={(e) => handleType(e.target.value)}
            onBlur={handleBlur}
            aria-label={`${label} value`}
            className="w-36 border-b border-transparent bg-transparent text-right font-display text-2xl text-[#10202E] transition-colors focus:border-emerald-600 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#8AA0B0]">
            {suffix}
          </span>
        </div>
      </div>
      <input
        id={testId}
        type="range"
        data-testid={testId}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="calc-range mt-4 w-full"
        style={{ "--fill": `${pct}%` }}
      />
    </div>
  );
};

const Stat = ({ label, value, testId, accent = false }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E8698]">{label}</p>
    <p
      data-testid={testId}
      className={`mt-1 font-display text-2xl ${accent ? "text-emerald-600" : "text-[#10202E]"}`}
    >
      {value}
    </p>
  </div>
);

const GrowthChart = ({ points, investedLine, maxV, testId }) => {
  const W = 320;
  const H = 150;
  const pad = 10;
  const max = Math.max(maxV, 1);
  const xAt = (idx) => pad + (idx / (points.length - 1)) * (W - 2 * pad);
  const yAt = (v) => H - pad - (v / max) * (H - 2 * pad);
  const line = points
    .map((v, idx) => `${idx === 0 ? "M" : "L"}${xAt(idx).toFixed(1)} ${yAt(v).toFixed(1)}`)
    .join(" ");
  const area = `${line} L${xAt(points.length - 1).toFixed(1)} ${H - pad} L${pad} ${H - pad} Z`;
  const invLine = investedLine
    .map((v, idx) => `${idx === 0 ? "M" : "L"}${xAt(idx).toFixed(1)} ${yAt(v).toFixed(1)}`)
    .join(" ");
  return (
    <>
      <svg data-testid={testId} viewBox={`0 0 ${W} ${H}`} className="mt-8 w-full" aria-hidden="true">
        <defs>
          <linearGradient id={`calcFill-${testId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(5,150,105,0.25)" />
            <stop offset="100%" stopColor="rgba(5,150,105,0)" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#calcFill-${testId})`} />
        <path d={invLine} stroke="rgba(16,32,46,0.3)" strokeWidth="1.2" strokeDasharray="4 5" fill="none" />
        <path d={line} stroke="#059669" strokeWidth="2" fill="none" strokeLinecap="round" />
        <circle cx={xAt(points.length - 1)} cy={yAt(points[points.length - 1])} r="4" fill="#D9A420" />
      </svg>
      <div className="mt-3 flex items-center gap-6">
        <span className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#6E8698]">
          <span className="h-1.5 w-4 rounded-full bg-emerald-600" /> Projected value
        </span>
        <span className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#6E8698]">
          <span className="h-px w-4 border-t border-dashed border-[#10202E]/40" /> You invest
        </span>
      </div>
    </>
  );
};

const EmailShare = ({ tool, lines }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const send = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setState("error");
      return;
    }
    setState("sending");
    try {
      await axios.post(`${API}/tools/share`, { email: email.trim(), tool, lines });
      setState("sent");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="mt-8 border-t border-[#10202E]/10 pt-6">
      {state === "sent" ? (
        <p data-testid="share-success" className="flex items-center gap-2 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4" /> Sent — check your inbox.
        </p>
      ) : !open ? (
        <button
          type="button"
          data-testid="share-toggle"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-700 transition-colors hover:text-emerald-600"
        >
          <Mail className="h-3.5 w-3.5" /> Email me this result
        </button>
      ) : (
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="email"
              data-testid="share-email-input"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (state === "error") setState("idle");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="you@example.com"
              className="min-w-0 flex-1 rounded-full border border-[#10202E]/20 bg-transparent px-4 py-2.5 text-sm text-[#10202E] placeholder:text-[#8AA0B0] focus:border-emerald-600 focus:outline-none"
            />
            <button
              type="button"
              data-testid="share-send-button"
              onClick={send}
              disabled={state === "sending"}
              className="inline-flex items-center gap-2 rounded-full bg-[#10202E] px-5 py-2.5 text-xs font-semibold text-[#F8F7F3] transition-colors hover:bg-emerald-600 disabled:opacity-60"
            >
              {state === "sending" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Send"}
            </button>
          </div>
          {state === "error" && (
            <p data-testid="share-error" className="mt-2 text-xs text-red-500">
              Enter a valid email address, or try again.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const ResultPanel = ({ bigLabel, bigValue, bigTestId, stats, chart, note, share, cta, ctaTestId }) => (
  <div className="flex h-full flex-col rounded-3xl border border-[#10202E]/[0.08] bg-white p-8 shadow-[0_2px_24px_rgba(16,32,46,0.05)] sm:p-10">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E8698]">
      {bigLabel}
    </span>
    <div className="mt-2 overflow-hidden">
      <motion.span
        key={Math.round(bigValue)}
        data-testid={bigTestId}
        initial={{ y: 18, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: EASE }}
        className="block font-display text-5xl text-emerald-600 sm:text-6xl"
      >
        {fmtFull(bigValue)}
      </motion.span>
    </div>
    <div className="mt-8 grid grid-cols-2 gap-6">{stats}</div>
    {chart}
    <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-[0.12em] text-[#8AA0B0]">
      {note}
    </p>
    {share && <EmailShare tool={share.tool} lines={share.lines} />}
    <button
      type="button"
      data-testid={ctaTestId}
      onClick={() => scrollToId("contact")}
      className="group mt-8 inline-flex w-fit items-center gap-2 rounded-full border border-[#10202E]/20 px-6 py-3 text-sm font-semibold text-[#1C3347] transition-[background-color,border-color,color] duration-300 hover:border-emerald-400 hover:bg-emerald-400 hover:text-[#04120c]"
    >
      {cta}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </button>
  </div>
);

const SliderCard = ({ children, note }) => (
  <div className="flex h-full flex-col justify-between gap-10 rounded-3xl border border-[#10202E]/[0.08] bg-white p-8 shadow-[0_2px_24px_rgba(16,32,46,0.05)] sm:p-10">
    {children}
    <p className="font-mono text-[10px] leading-relaxed tracking-[0.12em] text-[#8AA0B0]">{note}</p>
  </div>
);

const GoalTool = ({ state, setState }) => {
  const { sip, futureCost, invested, pts, inv } = useMemo(() => {
    const futureCost = state.target * Math.pow(1 + state.infl / 100, state.years);
    const sip = sipFor(futureCost, state.years, state.ret);
    const { pts, inv } = seriesFor(sip, state.years, state.ret);
    return { sip, futureCost, invested: sip * state.years * 12, pts, inv };
  }, [state]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <SliderCard note="INFLATION ADJUSTED — THE GOAL IS COSTED AT WHAT IT WILL ACTUALLY COST WHEN YOU GET THERE. ILLUSTRATIVE ONLY; RETURNS ARE NOT GUARANTEED.">
        <Slider label="Cost of the goal today" suffix="₹" value={state.target} min={500000} max={50000000} step={100000} onChange={(v) => setState({ ...state, target: v })} testId="calc-target-slider" />
        <Slider label="Time horizon" suffix="yrs" value={state.years} min={1} max={30} step={1} onChange={(v) => setState({ ...state, years: v })} testId="calc-years-slider" />
        <Slider label="Inflation" suffix="% p.a." value={state.infl} min={4} max={8} step={0.5} onChange={(v) => setState({ ...state, infl: v })} testId="calc-inflation-slider" />
        <Slider label="Expected return" suffix="% p.a." value={state.ret} min={8} max={15} step={0.5} onChange={(v) => setState({ ...state, ret: v })} testId="calc-return-slider" />
      </SliderCard>
      <ResultPanel
        bigLabel="Monthly SIP needed"
        bigValue={sip}
        bigTestId="calc-sip-result"
        stats={[
          <Stat key="f" label={`Cost in ${state.years} yrs (inflation-adj.)`} value={fmtINR(futureCost)} testId="calc-future-cost" accent />,
          <Stat key="i" label="You invest" value={fmtINR(invested)} testId="calc-invested-value" />,
        ]}
        chart={<GrowthChart points={pts} investedLine={inv} maxV={futureCost} testId="calc-chart" />}
        note="ASSUMES MONTHLY INVESTMENTS COMPOUNDED AT THE EXPECTED RETURN, INVESTED AT THE START OF EACH MONTH."
        share={{
          tool: "Goal SIP",
          lines: [
            `Goal cost today: ${fmtFull(state.target)}`,
            `Inflation-adjusted cost in ${state.years} years: ${fmtFull(futureCost)}`,
            `Monthly SIP needed: ${fmtFull(sip)}`,
            `Total you invest: ${fmtFull(invested)}`,
            `Assumptions: ${state.ret}% p.a. return, ${state.infl}% inflation`,
          ],
        }}
        cta="Plan this goal with us"
        ctaTestId="calc-cta"
      />
    </div>
  );
};

const RegularTool = ({ state, setState }) => {
  const { corpus, invested, growth, todayValue, pts, inv } = useMemo(() => {
    const i = state.ret / 100 / 12;
    const n = state.years * 12;
    const corpus = state.monthly * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
    const invested = state.monthly * n;
    const todayValue = corpus / Math.pow(1 + state.infl / 100, state.years);
    const { pts, inv } = seriesFor(state.monthly, state.years, state.ret);
    return { corpus, invested, growth: corpus - invested, todayValue, pts, inv };
  }, [state]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <SliderCard note="INFLATION ADJUSTED — 'IN TODAY'S MONEY' SHOWS WHAT YOUR CORPUS WOULD BUY AT TODAY'S PRICES. ILLUSTRATIVE ONLY; RETURNS ARE NOT GUARANTEED.">
        <Slider label="Monthly investment" suffix="₹ / mo" value={state.monthly} min={500} max={500000} step={500} onChange={(v) => setState({ ...state, monthly: v })} testId="regular-amount-slider" />
        <Slider label="Time horizon" suffix="yrs" value={state.years} min={1} max={30} step={1} onChange={(v) => setState({ ...state, years: v })} testId="regular-years-slider" />
        <Slider label="Expected return" suffix="% p.a." value={state.ret} min={8} max={15} step={0.5} onChange={(v) => setState({ ...state, ret: v })} testId="regular-return-slider" />
        <Slider label="Inflation" suffix="% p.a." value={state.infl} min={4} max={8} step={0.5} onChange={(v) => setState({ ...state, infl: v })} testId="regular-inflation-slider" />
      </SliderCard>
      <ResultPanel
        bigLabel="Projected corpus"
        bigValue={corpus}
        bigTestId="regular-corpus-result"
        stats={[
          <Stat key="i" label="You invest" value={fmtINR(invested)} testId="regular-invested-value" />,
          <Stat key="g" label="Growth adds" value={fmtINR(growth)} testId="regular-growth-value" accent />,
          <Stat key="t" label="In today's money" value={fmtINR(todayValue)} testId="regular-today-value" />,
        ]}
        chart={<GrowthChart points={pts} investedLine={inv} maxV={corpus} testId="regular-chart" />}
        note="ASSUMES MONTHLY INVESTMENTS COMPOUNDED AT THE EXPECTED RETURN, INVESTED AT THE START OF EACH MONTH."
        share={{
          tool: "Regular SIP",
          lines: [
            `Monthly investment: ${fmtFull(state.monthly)}`,
            `Projected corpus in ${state.years} years: ${fmtFull(corpus)}`,
            `Total invested: ${fmtFull(invested)}`,
            `Growth adds: ${fmtFull(growth)}`,
            `In today's money (${state.infl}% inflation): ${fmtFull(todayValue)}`,
            `Assumed return: ${state.ret}% p.a.`,
          ],
        }}
        cta="Start this SIP with us"
        ctaTestId="regular-cta"
      />
    </div>
  );
};

const RetirementTool = ({ state, setState }) => {
  const { sip, corpus, futureExpense, pts, inv } = useMemo(() => {
    const futureExpense = state.expense * Math.pow(1 + state.infl / 100, state.years);
    const futureAnnual = futureExpense * 12;
    const real = (1 + state.ret / 100) / (1 + state.infl / 100) - 1;
    const corpus =
      real <= 0 ? futureAnnual * 25 : futureAnnual * ((1 - Math.pow(1 + real, -25)) / real);
    const sip = sipFor(corpus, state.years, state.ret);
    const { pts, inv } = seriesFor(sip, state.years, state.ret);
    return { sip, corpus, futureExpense, pts, inv };
  }, [state]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <SliderCard note="INFLATION ADJUSTED — YOUR EXPENSES AT RETIREMENT WILL COST MORE THAN THEY DO TODAY.">
        <Slider label="Monthly expense today" suffix="₹ / mo" value={state.expense} min={20000} max={500000} step={5000} onChange={(v) => setState({ ...state, expense: v })} testId="retire-expense-slider" />
        <Slider label="Years to retirement" suffix="yrs" value={state.years} min={5} max={35} step={1} onChange={(v) => setState({ ...state, years: v })} testId="retire-years-slider" />
        <Slider label="Inflation" suffix="% p.a." value={state.infl} min={4} max={8} step={0.5} onChange={(v) => setState({ ...state, infl: v })} testId="retire-inflation-slider" />
        <Slider label="Expected return" suffix="% p.a." value={state.ret} min={9} max={13} step={0.5} onChange={(v) => setState({ ...state, ret: v })} testId="retire-return-slider" />
      </SliderCard>
      <ResultPanel
        bigLabel="Monthly SIP needed"
        bigValue={sip}
        bigTestId="retire-sip-result"
        stats={[
          <Stat key="e" label="Monthly expense at retirement" value={fmtFull(futureExpense)} testId="retire-expense-future" />,
          <Stat key="c" label="Corpus needed" value={fmtINR(corpus)} testId="retire-corpus" accent />,
        ]}
        chart={<GrowthChart points={pts} investedLine={inv} maxV={corpus} testId="retire-chart" />}
        note="ASSUMES A 25-YEAR RETIREMENT FUNDED AT INFLATION-ADJUSTED WITHDRAWALS. ILLUSTRATIVE ONLY — RETURNS ARE NOT GUARANTEED."
        share={{
          tool: "Retirement",
          lines: [
            `Monthly expense today: ${fmtFull(state.expense)}`,
            `Monthly expense at retirement: ${fmtFull(futureExpense)}`,
            `Corpus needed (25-year retirement): ${fmtFull(corpus)}`,
            `Monthly SIP needed: ${fmtFull(sip)}`,
            `Assumptions: ${state.ret}% return, ${state.infl}% inflation, ${state.years} yrs to retirement`,
          ],
        }}
        cta="Talk about retirement"
        ctaTestId="retire-cta"
      />
    </div>
  );
};

const EducationTool = ({ state, setState }) => {
  const { sip, futureCost, lumpsum, pts, inv } = useMemo(() => {
    const futureCost = state.cost * Math.pow(1 + state.infl / 100, state.years);
    const lumpsum = futureCost / Math.pow(1 + state.ret / 100, state.years);
    const sip = sipFor(futureCost, state.years, state.ret);
    const { pts, inv } = seriesFor(sip, state.years, state.ret);
    return { sip, futureCost, lumpsum, pts, inv };
  }, [state]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <SliderCard note="EDUCATION INFLATION IN INDIA HAS HISTORICALLY OUTPACED GENERAL INFLATION — PLAN FOR THE FUTURE COST, NOT TODAY'S.">
        <Slider label="Cost of education today" suffix="₹" value={state.cost} min={500000} max={10000000} step={100000} onChange={(v) => setState({ ...state, cost: v })} testId="edu-cost-slider" />
        <Slider label="Years until needed" suffix="yrs" value={state.years} min={1} max={18} step={1} onChange={(v) => setState({ ...state, years: v })} testId="edu-years-slider" />
        <Slider label="Education inflation" suffix="% p.a." value={state.infl} min={6} max={12} step={0.5} onChange={(v) => setState({ ...state, infl: v })} testId="edu-inflation-slider" />
        <Slider label="Expected return" suffix="% p.a." value={state.ret} min={9} max={13} step={0.5} onChange={(v) => setState({ ...state, ret: v })} testId="edu-return-slider" />
      </SliderCard>
      <ResultPanel
        bigLabel="Monthly SIP needed"
        bigValue={sip}
        bigTestId="edu-sip-result"
        stats={[
          <Stat key="f" label={`Cost in ${state.years} yrs`} value={fmtINR(futureCost)} testId="edu-future-cost" accent />,
          <Stat key="l" label="Or as a lump sum today" value={fmtINR(lumpsum)} testId="edu-lumpsum" />,
        ]}
        chart={<GrowthChart points={pts} investedLine={inv} maxV={futureCost} testId="edu-chart" />}
        note="EDUCATION INFLATION ASSUMED AS SET ABOVE. ILLUSTRATIVE ONLY — MUTUAL FUND RETURNS ARE NOT GUARANTEED."
        share={{
          tool: "Child's Education",
          lines: [
            `Education cost today: ${fmtFull(state.cost)}`,
            `Cost in ${state.years} years (${state.infl}% education inflation): ${fmtFull(futureCost)}`,
            `Monthly SIP needed: ${fmtFull(sip)}`,
            `Or as a lump sum today: ${fmtFull(lumpsum)}`,
            `Assumed return: ${state.ret}% p.a.`,
          ],
        }}
        cta="Plan for their future"
        ctaTestId="edu-cta"
      />
    </div>
  );
};

const StepUpTool = ({ state, setState }) => {
  const { corpus, invested, growth, bonus, pts, inv } = useMemo(() => {
    const i = state.ret / 100 / 12;
    let value = 0;
    let invested = 0;
    const pts = [0];
    const inv = [0];
    for (let y = 1; y <= state.years; y += 1) {
      const mAmt = state.monthly * Math.pow(1 + state.step / 100, y - 1);
      for (let m = 0; m < 12; m += 1) {
        value = value * (1 + i) + mAmt;
        invested += mAmt;
      }
      pts.push(value);
      inv.push(invested);
    }
    const flat =
      state.monthly * ((Math.pow(1 + i, state.years * 12) - 1) / i) * (1 + i);
    return { corpus: value, invested, growth: value - invested, bonus: value - flat, pts, inv };
  }, [state]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <SliderCard note="A STEP-UP (TOP-UP) SIP RAISES YOUR MONTHLY INVESTMENT EVERY YEAR — TYPICALLY IN LINE WITH SALARY GROWTH. ILLUSTRATIVE ONLY; RETURNS ARE NOT GUARANTEED.">
        <Slider label="Starting monthly SIP" suffix="₹ / mo" value={state.monthly} min={500} max={200000} step={500} onChange={(v) => setState({ ...state, monthly: v })} testId="stepup-amount-slider" />
        <Slider label="Annual step-up" suffix="% / yr" value={state.step} min={0} max={20} step={1} onChange={(v) => setState({ ...state, step: v })} testId="stepup-step-slider" />
        <Slider label="Time horizon" suffix="yrs" value={state.years} min={1} max={30} step={1} onChange={(v) => setState({ ...state, years: v })} testId="stepup-years-slider" />
        <Slider label="Expected return" suffix="% p.a." value={state.ret} min={8} max={15} step={0.5} onChange={(v) => setState({ ...state, ret: v })} testId="stepup-return-slider" />
      </SliderCard>
      <ResultPanel
        bigLabel="Projected corpus"
        bigValue={corpus}
        bigTestId="stepup-corpus-result"
        stats={[
          <Stat key="i" label="You invest" value={fmtINR(invested)} testId="stepup-invested-value" />,
          <Stat key="g" label="Growth adds" value={fmtINR(growth)} testId="stepup-growth-value" accent />,
          <Stat key="b" label="vs flat SIP" value={`+${fmtINR(Math.max(0, bonus))}`} testId="stepup-bonus" accent />,
        ]}
        chart={<GrowthChart points={pts} investedLine={inv} maxV={corpus} testId="stepup-chart" />}
        note={`YOUR SIP RISES ${state.step}% EVERY YEAR. THE "VS FLAT SIP" FIGURE SHOWS WHAT THAT STEP-UP ALONE ADDS OVER A FIXED ${fmtINR(state.monthly)} SIP.`}
        share={{
          tool: "Step-Up SIP",
          lines: [
            `Starting monthly SIP: ${fmtFull(state.monthly)}`,
            `Annual step-up: ${state.step}%`,
            `Projected corpus in ${state.years} years: ${fmtFull(corpus)}`,
            `Total invested: ${fmtFull(invested)}`,
            `Growth adds: ${fmtFull(growth)}`,
            `Advantage vs flat SIP: ${fmtFull(Math.max(0, bonus))}`,
            `Assumed return: ${state.ret}% p.a.`,
          ],
        }}
        cta="Set up a step-up SIP"
        ctaTestId="stepup-cta"
      />
    </div>
  );
};

const EmergencyTool = ({ state, setState }) => {
  const { target, gap, monthly, pts, inv } = useMemo(() => {
    const target = state.expense * state.months;
    const gap = Math.max(0, target - state.savings);
    const i = state.ret / 100 / 12;
    const n = state.build;
    const monthly = gap > 0 ? gap / (((Math.pow(1 + i, n) - 1) / i) * (1 + i)) : 0;
    const pts = [];
    const inv = [];
    for (let m = 0; m <= n; m += 1) {
      pts.push(
        m === 0 ? state.savings : state.savings + monthly * ((Math.pow(1 + i, m) - 1) / i) * (1 + i),
      );
      inv.push(state.savings + monthly * m);
    }
    return { target, gap, monthly, pts, inv };
  }, [state]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <SliderCard note="EMERGENCY MONEY PRIORITISES ACCESS OVER RETURNS — IT IS TYPICALLY PARKED IN LIQUID FUNDS OR SWEEP-IN DEPOSITS. ILLUSTRATIVE ONLY.">
        <Slider label="Essential monthly expenses" suffix="₹ / mo" value={state.expense} min={10000} max={500000} step={5000} onChange={(v) => setState({ ...state, expense: v })} testId="em-expense-slider" />
        <Slider label="Cover needed" suffix="months" value={state.months} min={3} max={12} step={1} onChange={(v) => setState({ ...state, months: v })} testId="em-months-slider" />
        <Slider label="Current emergency savings" suffix="₹" value={state.savings} min={0} max={20000000} step={50000} onChange={(v) => setState({ ...state, savings: v })} testId="em-savings-slider" />
        <Slider label="Build it over" suffix="months" value={state.build} min={6} max={36} step={1} onChange={(v) => setState({ ...state, build: v })} testId="em-build-slider" />
        <Slider label="Return while building" suffix="% p.a." value={state.ret} min={5} max={7} step={0.25} onChange={(v) => setState({ ...state, ret: v })} testId="em-return-slider" />
      </SliderCard>
      <ResultPanel
        bigLabel={gap > 0 ? "Monthly build needed" : "You are fully covered"}
        bigValue={monthly}
        bigTestId="em-sip-result"
        stats={[
          <Stat key="t" label={`Fund target (${state.months} months)`} value={fmtINR(target)} testId="em-target" />,
          <Stat key="g" label="Gap to build" value={gap > 0 ? fmtINR(gap) : "None"} testId="em-gap" accent />,
        ]}
        chart={<GrowthChart points={pts} investedLine={inv} maxV={target} testId="em-chart" />}
        note="THE CHART STARTS FROM YOUR CURRENT SAVINGS. KEEP THIS FUND SEPARATE FROM INVESTMENTS MEANT FOR GROWTH."
        share={{
          tool: "Emergency Fund",
          lines: [
            `Essential monthly expenses: ${fmtFull(state.expense)}`,
            `Emergency fund target (${state.months} months): ${fmtFull(target)}`,
            `Current emergency savings: ${fmtFull(state.savings)}`,
            `Gap to build: ${fmtFull(gap)}`,
            `Monthly build needed over ${state.build} months: ${fmtFull(monthly)}`,
            `Assumed return while building: ${state.ret}% p.a.`,
          ],
        }}
        cta="Build my safety net"
        ctaTestId="em-cta"
      />
    </div>
  );
};

const TABS = [
  { id: "goal", label: "Goal SIP", tint: "#FFF7C7" },
  { id: "regular", label: "Regular SIP", tint: "#DDF7EC" },
  { id: "stepup", label: "Step-Up SIP", tint: "#DDF7EC" },
  { id: "emergency", label: "Emergency Fund", tint: "#E8EFF4" },
  { id: "retirement", label: "Retirement", tint: "#DDF4FC" },
  { id: "education", label: "Child's Education", tint: "#FBE5F1" },
];

export const Calculator = () => {
  const [tab, setTab] = useState("goal");
  const [goal, setGoal] = useState({ target: 5000000, years: 15, infl: 6, ret: 12 });
  const [regular, setRegular] = useState({ monthly: 10000, years: 15, ret: 12, infl: 6 });
  const [retire, setRetire] = useState({ expense: 60000, years: 25, infl: 6, ret: 11 });
  const [edu, setEdu] = useState({ cost: 2500000, years: 10, infl: 10, ret: 12 });
  const [stepup, setStepup] = useState({ monthly: 10000, step: 10, years: 15, ret: 12 });
  const [emergency, setEmergency] = useState({ expense: 60000, months: 6, savings: 100000, build: 12, ret: 6 });
  const activeTint = TABS.find((t) => t.id === tab).tint;

  return (
    <motion.section
      id="calculator"
      data-testid="calculator-section"
      animate={{ backgroundColor: activeTint }}
      transition={{ duration: 0.9, ease: EASE }}
      className="relative bg-[#FFF7C7] py-28 md:py-40"
    >
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <Eyebrow>06 — Planning Tools</Eyebrow>
        </Reveal>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-8">
          <Reveal delay={0.08}>
            <h2 className="max-w-2xl font-display text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              Every dream has a number. <span className="italic text-emerald-600">Find yours.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="max-w-sm text-base font-light leading-relaxed text-[#4E6A82]">
              Honest estimates for the goals that matter most — inflation included. Drag a slider
              or type a number directly.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.18}>
          <div className="mt-12 inline-flex flex-wrap gap-1.5 rounded-full border border-[#10202E]/10 bg-white/70 p-1.5 backdrop-blur-sm">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`calc-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                  tab === t.id ? "text-[#F8F7F3]" : "text-[#4E6A82] hover:text-[#10202E]"
                }`}
              >
                {tab === t.id && (
                  <motion.span
                    layoutId="calc-tab-pill"
                    transition={{ duration: 0.5, ease: EASE }}
                    className="absolute inset-0 rounded-full bg-[#10202E]"
                  />
                )}
                <span className="relative">{t.label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              {tab === "goal" && <GoalTool state={goal} setState={setGoal} />}
              {tab === "regular" && <RegularTool state={regular} setState={setRegular} />}
              {tab === "stepup" && <StepUpTool state={stepup} setState={setStepup} />}
              {tab === "emergency" && <EmergencyTool state={emergency} setState={setEmergency} />}
              {tab === "retirement" && <RetirementTool state={retire} setState={setRetire} />}
              {tab === "education" && <EducationTool state={edu} setState={setEdu} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.section>
  );
};
